import { MP_METHODS, type PaymentMethod, type SaleDTO, type SaleInput } from '@bifeai/shared';
import { useSyncExternalStore } from 'react';
import { api, ApiError, isApiError } from './api';

/**
 * Modo sin internet del punto de venta.
 *
 * Si se corta la conexión, la venta se guarda en esta computadora con su hora real y los precios que
 * tenía la caja, se imprime un ticket provisorio y se sigue vendiendo. Cuando vuelve internet, las
 * ventas se suben solas (con el mismo identificador: si una ya había llegado, no se duplica).
 * Lo que necesita conexión (Mercado Pago, fiado, factura con datos del cliente) no se ofrece.
 *
 * También se guarda una copia de la sesión, la caja y el catálogo del mostrador para que la pantalla
 * de venta abra aunque se recargue la página sin internet.
 */

export interface PendingSale {
  clientId: string;
  /** Quien la cobró: se sube con su sesión (otro usuario en la misma computadora no la sube a su nombre). */
  userId: string;
  body: SaleInput;
  soldAt: string;
  totalCents: number;
  cashierName: string;
  /** Si el servidor la rechazó al subirla: el motivo, para resolverla a mano. */
  error: string | null;
}

const QUEUE_KEY = 'bifeai.offline.sales.v1';
const SNAP_PREFIX = 'bifeai.offline.snap.v1:';

/** Medios de pago que se pueden usar sin internet. */
export const OFFLINE_METHODS: PaymentMethod[] = ['CASH', 'DEBIT', 'CREDIT', 'TRANSFER', 'QR', 'CUENTA_DNI', 'OTHER'];
export const offlineAllowed = (m: PaymentMethod) => OFFLINE_METHODS.includes(m) && !MP_METHODS.includes(m);

/** Error de red (no llegó al servidor), no un rechazo de la API. */
export const isNetworkError = (err: unknown) => err instanceof ApiError && err.status === 0;

// ---------------------------------------------------------------- estado compartido
type State = { online: boolean; queue: PendingSale[]; syncing: boolean };

function readQueue(): PendingSale[] {
  try {
    const raw = window.localStorage.getItem(QUEUE_KEY);
    return raw ? (JSON.parse(raw) as PendingSale[]) : [];
  } catch {
    return [];
  }
}

let state: State = {
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  queue: typeof window === 'undefined' ? [] : readQueue(),
  syncing: false,
};
const listeners = new Set<() => void>();
function set(patch: Partial<State>) {
  state = { ...state, ...patch };
  if (patch.queue) {
    try {
      window.localStorage.setItem(QUEUE_KEY, JSON.stringify(patch.queue));
    } catch {
      // Sin lugar en el navegador: la venta igual queda en memoria hasta que se suba.
    }
  }
  listeners.forEach((l) => l());
}

/** Ventas de este usuario que esperan subirse y las que el servidor rechazó. */
export function useOffline(userId: string): State & { pending: number; failed: PendingSale[] } {
  const s = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state,
  );
  const mine = s.queue.filter((p) => p.userId === userId);
  return { ...s, pending: mine.filter((p) => !p.error).length, failed: mine.filter((p) => p.error) };
}

/** Avisan las llamadas a la API: si falló la red, estamos sin conexión; si respondió, volvió. */
export function markOnline(online: boolean): void {
  if (state.online !== online) set({ online });
  if (online) void syncPending();
}

/** Guarda una venta cobrada sin internet. */
export function enqueueSale(body: SaleInput, user: { id: string; name: string }): PendingSale {
  const soldAt = body.offlineAt ?? new Date().toISOString();
  const item: PendingSale = {
    clientId: body.clientId,
    userId: user.id,
    body: { ...body, offlineAt: soldAt },
    soldAt,
    totalCents: body.payments.reduce((a, p) => a + p.amountCents, 0),
    cashierName: user.name,
    error: null,
  };
  set({ queue: [...state.queue.filter((q) => q.clientId !== item.clientId), item] });
  return item;
}

/** Vuelve a intentar subir las ventas que el servidor rechazó (por ejemplo, la caja estaba cerrada). */
export function retryFailed(): Promise<void> {
  set({ queue: state.queue.map((q) => (q.userId === currentUser ? { ...q, error: null } : q)) });
  return syncPending();
}

/** Quita una venta rechazada que ya se resolvió a mano. */
export function discardSale(clientId: string): void {
  set({ queue: state.queue.filter((q) => q.clientId !== clientId) });
}

let currentUser: string | null = null;
/** Usuario con la sesión abierta en esta computadora. */
export function setOfflineUser(id: string | null): void {
  currentUser = id;
}

let running: Promise<void> | null = null;
/** Sube las ventas pendientes, de la más vieja a la más nueva. */
export function syncPending(): Promise<void> {
  if (running) return running;
  const todo = state.queue.filter((q) => !q.error && q.userId === currentUser);
  if (!todo.length) return Promise.resolve();
  running = (async () => {
    set({ syncing: true });
    try {
      for (const p of todo) {
        try {
          await api.post<SaleDTO>('/sales', p.body);
          set({ queue: state.queue.filter((q) => q.clientId !== p.clientId), online: true });
        } catch (err) {
          if (isNetworkError(err)) {
            set({ online: false });
            break;
          }
          // Sesión vencida: se reintenta cuando vuelva a ingresar.
          if (isApiError(err, 'UNAUTHENTICATED')) break;
          const message = err instanceof Error ? err.message : 'No se pudo subir.';
          set({ queue: state.queue.map((q) => (q.clientId === p.clientId ? { ...q, error: message } : q)) });
        }
      }
    } finally {
      set({ syncing: false });
      running = null;
    }
  })();
  return running;
}

/** Reintento automático: al volver la conexión y cada 20 segundos mientras haya pendientes. */
export function startOfflineSync(): () => void {
  const onOnline = () => markOnline(true);
  const onOffline = () => set({ online: false });
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  window.addEventListener('bifeai:online', onOnline);
  window.addEventListener('bifeai:offline', onOffline);
  const timer = window.setInterval(() => {
    if (state.queue.some((q) => !q.error && q.userId === currentUser)) void syncPending();
  }, 20_000);
  void syncPending();
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    window.removeEventListener('bifeai:online', onOnline);
    window.removeEventListener('bifeai:offline', onOffline);
    window.clearInterval(timer);
  };
}

// ---------------------------------------------------------------- copias para abrir sin internet
export function saveSnapshot<T>(key: string, data: T): void {
  try {
    window.localStorage.setItem(SNAP_PREFIX + key, JSON.stringify(data));
  } catch {
    // Sin lugar: no pasa nada, solo no se podrá abrir sin internet.
  }
}

export function readSnapshot<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(SNAP_PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/** Al salir de la cuenta se borran las copias (las ventas pendientes quedan para subirlas). */
export function clearSnapshots(): void {
  try {
    for (const k of Object.keys(window.localStorage)) if (k.startsWith(SNAP_PREFIX)) window.localStorage.removeItem(k);
  } catch {
    // nada
  }
}

/**
 * Consulta con copia local: si responde, se guarda la copia; si no hay red, se usa la última copia.
 */
export async function withSnapshot<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  try {
    const data = await fetcher();
    saveSnapshot(key, data);
    return data;
  } catch (err) {
    if (isNetworkError(err)) {
      const snap = readSnapshot<T>(key);
      if (snap) return snap;
    }
    throw err;
  }
}
